import { useEffect, useMemo } from 'react';
import { formatAstuteOccupationLibraryLabel } from '@core/integration/integration.actions';
import { useIntegrationStore } from '@core/integration/useIntegrationStore';
import Button from '@/components/Button';
import { useTenantId } from '@/contexts/TenantContext';
import PayrollDataTable from '@/modules/payroll/components/shared/PayrollDataTable';
import PayrollDateRangePicker from '@/modules/payroll/components/shared/PayrollDateRangePicker';
import { usePayRunReadiness } from '@/modules/payroll/hooks/usePayRunReadiness';
import {
  OCCUPATION_LIBRARY_OID_HEADER,
  withOccupationLibraryColumn,
} from '@/modules/payroll/utils/payRunReadiness.utils';
import type { PayrollView } from '@/modules/payroll/views/payroll.view';

type PayRunReadinessTabProps = {
  view: PayrollView;
};

/**
 * Pay run readiness report: one row per shift in the date range with whether
 * it is ready to be paid and the integration references behind it.
 */
const PayRunReadinessTab = ({ view }: PayRunReadinessTabProps) => {
  const tenantId = useTenantId();
  const report = usePayRunReadiness(tenantId);
  const hasOccupationLibraryColumn = report.headers.includes(
    OCCUPATION_LIBRARY_OID_HEADER,
  );
  const ensureAstuteOccupationLibraries = useIntegrationStore(
    (state) => state.ensureAstuteOccupationLibraries,
  );
  const occupationLibraries = useIntegrationStore((state) =>
    tenantId ? state.astuteOccupationLibrariesByTenantId[tenantId] : undefined,
  );

  // Jobs only carry the library OID; the names come from the tenant's list.
  useEffect(() => {
    if (!tenantId || !hasOccupationLibraryColumn) {
      return;
    }
    void ensureAstuteOccupationLibraries(tenantId).catch((error) => {
      console.error('Unable to load Astute occupation libraries', error);
    });
  }, [ensureAstuteOccupationLibraries, hasOccupationLibraryColumn, tenantId]);

  const table = useMemo(() => {
    const labelByOid = occupationLibraries
      ? Object.fromEntries(
          occupationLibraries.map((library) => [
            library.OL_OID,
            formatAstuteOccupationLibraryLabel(library),
          ]),
        )
      : null;
    const { headers, records } = withOccupationLibraryColumn(
      report.headers,
      report.records,
      labelByOid,
    );
    return {
      headers,
      rows: records.map((record) =>
        headers.map((header) => record[header] ?? ''),
      ),
    };
  }, [report.headers, report.records, occupationLibraries]);

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-white p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PayrollDateRangePicker
          value={report.dateRange}
          onChange={report.setDateRange}
        />
        <div className="flex gap-3">
          {view.actions.showExportReadiness && (
            <Button
              variant="outline"
              className="py-2 text-sm"
              onClick={report.exportCsv}
              disabled={report.isLoading || report.records.length === 0}
            >
              Export CSV
            </Button>
          )}
          <Button
            variant="primary"
            className="py-2 text-sm"
            onClick={() => void report.runReport()}
            isLoading={report.isLoading}
          >
            Run report
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Defaults to the latest completed Monday–Sunday pay week.
      </p>

      {report.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {report.error}
        </div>
      )}

      {report.isLoading ? (
        <p className="py-6 text-sm text-gray-500">Loading pay run readiness…</p>
      ) : report.hasRun ? (
        <PayrollDataTable
          columns={table.headers}
          rows={table.rows}
          sortable
          emptyMessage="No pay run readiness rows found for this date range."
        />
      ) : (
        <p className="py-6 text-sm text-gray-500">
          Choose a date range and run the report.
        </p>
      )}
    </div>
  );
};

export default PayRunReadinessTab;
